import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import '@/features/f0/styles/bodyFonts'
import '@/features/f0/ui'
import './t0.css'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { usePreferencesStore } from '@/stores/preferencesStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useIsDesktop } from '@/features/f0/shell/useIsDesktop'
import type { User } from '@/types'
import { GUIDE_ANCHORS, isAvailable, placementOf, type Capabilities } from './registry'
import { currentStep, initialState, reduce, type ChapterRecord, type TutorialEvent } from './machine'
import { currentSeq, emitTutorialEvent, resetTutorialEvents, subscribeTutorialEvents } from './events'
import { createProgressSync } from './progress'
import { useTutorialUi } from './open'
import StepCard, { type StepCardProps } from './StepCard'
import Spotlight, { findAnchor, useAnchorRect } from './Spotlight'
import ChapterDrawer from './ChapterDrawer'
import WelcomeOffer from './WelcomeOffer'
import AccountStatusCard from './AccountStatusCard'

const POLL_MS = 150
const ANCHOR_WAIT_MS = 6000
const CARD_W = 400
const CARD_H = 360
const ACTIVE = ['waiting', 'showing', 'paused', 'chapterDone', 'finished']

/** Same pathname, and every query param the route names is already in the URL. */
function atRoute(loc: { pathname: string; search: string }, route: string): boolean {
  const url = new URL(route, window.location.origin)
  if (url.pathname !== loc.pathname) return false
  const here = new URLSearchParams(loc.search)
  return [...url.searchParams].every(([k, v]) => here.get(k) === v)
}

function filtersOf(search: string): string {
  const params = new URLSearchParams(search)
  params.delete('view')
  params.sort()
  return params.toString()
}

function scrollParent(el: HTMLElement): Element {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p)
    if (/(auto|scroll)/.test(overflowY) && p.scrollHeight > p.clientHeight) return p
  }
  return document.scrollingElement ?? document.documentElement
}

// Mounted once in App.tsx (inside the Router, outside the shell, so /onboarding is covered).
export default function TutorialHost() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const userId = isAuthenticated ? user?.id ?? null : null
  const lastUser = useRef<string | null>(null)

  useEffect(() => {
    if (lastUser.current === userId) return
    lastUser.current = userId
    resetTutorialEvents()
    useTutorialUi.getState().closeDrawer()
  }, [userId])

  if (!userId || !user) return null
  return <Guide key={userId} user={user} />
}

function Guide({ user }: { user: User }) {
  const [state, dispatch] = useReducer(reduce, undefined, initialState)
  const stateRef = useRef(state)
  stateRef.current = state
  const navigate = useNavigate()
  const location = useLocation()
  const desktop = useIsDesktop()
  const drawerOpen = useTutorialUi((s) => s.drawerOpen)
  const onboardingStatus = useAuthStore((s) => s.onboardingStatus)
  const activeWorkspace = useWorkspaceStore((s) => s.activeWorkspace)
  const preferences = usePreferencesStore((s) => s.preferences)
  const prefsError = usePreferencesStore((s) => s.error)
  const [caps, setCaps] = useState<Capabilities | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const sync = useMemo(() => createProgressSync(user.id), [user.id])
  const saved = useRef<Record<string, string>>({})
  const returnFocus = useRef<HTMLElement | null>(null)

  const send: StepCardProps['send'] = useCallback((e) => dispatch({ ...e, at: currentSeq() } as TutorialEvent), [])
  const onboarded = onboardingStatus?.is_onboarded ?? activeWorkspace?.is_onboarded ?? false
  const workspaceId = activeWorkspace?.id ?? null

  useEffect(() => {
    let alive = true
    api.getSubscriptionInfo().then(
      (info) => alive && setCaps({
        can_use_ai_images: info.can_use_ai_images,
        can_use_carousels: info.can_use_carousels,
        can_use_learning_system: info.can_use_learning_system,
        can_use_company_intelligence: info.can_use_company_intelligence,
      }),
      () => undefined,
    )
    return () => { alive = false }
  }, [])

  useEffect(() => {
    dispatch({ type: 'CONTEXT', ctx: { authenticated: true, onboarded }, caps })
  }, [onboarded, caps])

  const prefsReady = preferences !== null || prefsError !== null
  // App.tsx fetches preferences only when a token exists at mount, not after an in-app login.
  useEffect(() => {
    if (!prefsReady) void usePreferencesStore.getState().fetchPreferences()
  }, [prefsReady])
  useEffect(() => {
    if (!prefsReady || hydrated) return
    let alive = true
    sync.load().then(({ chapters, hasRows }) => {
      if (!alive) return
      saved.current = Object.fromEntries(Object.entries(chapters).map(([k, v]) => [k, JSON.stringify(v)]))
      const offer = !hasRows && usePreferencesStore.getState().preferences?.has_completed_tutorial !== true
      dispatch({ type: 'HYDRATE', chapters, offer })
      // Left while running (not paused or exited): pick up where the page was left.
      if (!offer && Object.values(chapters).some((c) => c.status === 'in_progress')) {
        dispatch({ type: 'START', at: currentSeq(), workspaceId: useWorkspaceStore.getState().activeWorkspace?.id ?? null })
      }
      setHydrated(true)
    })
    return () => { alive = false }
  }, [prefsReady, hydrated, sync])

  useEffect(() => {
    if (!hydrated) return
    for (const [chapter, rec] of Object.entries(state.chapters)) {
      const json = JSON.stringify(rec)
      if (saved.current[chapter] === json) continue
      saved.current[chapter] = json
      sync.save(chapter, rec).then((r) => {
        if (r.kind === 'failed') delete saved.current[chapter]
        if (r.kind !== 'conflict') return
        saved.current = Object.fromEntries(Object.entries(r.chapters).map(([k, v]) => [k, JSON.stringify(v)]))
        dispatch({ type: 'HYDRATE', chapters: r.chapters as Record<string, ChapterRecord>, offer: false })
      })
    }
  }, [hydrated, state.chapters, sync])

  useEffect(() => subscribeTutorialEvents((e) => {
    dispatch(e.failed
      ? { type: 'REQUEST_FAILED', event: e.event }
      : { type: 'ACTION_CONFIRMED', event: e.event, seq: e.seq, workspaceId: e.workspaceId, entityId: e.entityId })
  }), [])

  const step = currentStep(state)
  const placement = placementOf(step, state.ctx)
  const active = ACTIVE.includes(state.mode)

  // Step entry: go to the step's page, then wait for its anchor (or explain why it is missing).
  useEffect(() => {
    if (state.mode !== 'waiting') return
    const s = stateRef.current
    const target = placementOf(currentStep(s), s.ctx)
    if (!isAvailable(currentStep(s), s.caps)) {
      dispatch({ type: 'ANCHOR_READY', stepId: currentStep(s).id })
      return
    }
    if (!(GUIDE_ANCHORS as readonly string[]).includes(target.anchor) && !atRoute(window.location, target.route)) {
      navigate(target.route)
    }
    const stepId = currentStep(s).id
    const started = Date.now()
    const timer = window.setInterval(() => {
      const el = findAnchor(target.anchor)
      if (el) {
        window.clearInterval(timer)
        el.scrollIntoView({ block: 'center', inline: 'nearest' })
        dispatch({ type: 'ANCHOR_READY', stepId })
      } else if (Date.now() - started >= ANCHOR_WAIT_MS) {
        window.clearInterval(timer)
        dispatch({ type: 'ANCHOR_TIMEOUT', stepId })
      }
    }, POLL_MS)
    return () => window.clearInterval(timer)
  }, [state.mode, state.index, state.since, navigate])

  // URL-driven events: leaving Home from T07.03, and filters changed on /content.
  const prevLocation = useRef(location)
  useEffect(() => {
    const prev = prevLocation.current
    prevLocation.current = location
    const s = stateRef.current
    if (!['waiting', 'showing'].includes(s.mode)) return
    const expects = currentStep(s).event
    const ws = { workspaceId: s.workspaceId }
    if (expects === 'destination-opened' && prev.pathname === '/app' && location.pathname !== '/app') {
      emitTutorialEvent('destination-opened', ws)
    }
    if (expects === 'filter-applied' && prev.pathname === '/content' && location.pathname === '/content'
      && filtersOf(prev.search) !== filtersOf(location.search)) {
      emitTutorialEvent('filter-applied', ws)
    }
  }, [location])

  // T18 teaches the drawer itself.
  useEffect(() => {
    if (step.chapter === 'T18' && ['waiting', 'showing'].includes(state.mode)) useTutorialUi.getState().openDrawer()
  }, [step.chapter, state.mode, state.index])

  // Focus goes back where it was when the guide closes.
  const wasActive = useRef(false)
  useEffect(() => {
    const on = active || state.mode === 'offer'
    if (on && !wasActive.current) {
      const el = document.activeElement as HTMLElement | null
      returnFocus.current = el && !el.closest('.t0') ? el : null
    }
    if (!on && wasActive.current) {
      const back = useTutorialUi.getState().invoker ?? returnFocus.current
      if (back?.isConnected) back.focus({ preventScroll: true })
    }
    wasActive.current = on
  }, [active, state.mode])

  const spotlit = state.mode === 'showing' && !state.empty && isAvailable(step, state.caps)
  const rect = useAnchorRect(spotlit ? placement.anchor : null)

  // At phone width the card sits at the bottom: room below the page lets any target scroll above it.
  useEffect(() => {
    const card = document.querySelector<HTMLElement>('[data-t0-card]')
    if (desktop || !active || !card) return
    document.body.style.paddingBottom = `${card.offsetHeight + 16}px`
    return () => { document.body.style.paddingBottom = '' }
  }, [desktop, active, step.id, state.mode])

  // At phone width the card sits at the bottom: keep the target above it.
  const scrolledFor = useRef<string | null>(null)
  useEffect(() => {
    if (desktop || !rect || scrolledFor.current === step.id) return
    scrolledFor.current = step.id
    const card = document.querySelector<HTMLElement>('[data-t0-card]')
    const el = findAnchor(placement.anchor)
    if (!card || !el) return
    const cardTop = card.getBoundingClientRect().top
    if (rect.bottom > cardTop - 8) scrollParent(el).scrollBy({ top: Math.min(rect.bottom - cardTop + 16, rect.top - 72) })
  }, [desktop, rect, step.id, placement.anchor])

  const side = desktop && rect && rect.right > window.innerWidth - CARD_W - 24 && rect.bottom > window.innerHeight - CARD_H - 24 ? 'left' : 'right'
  const hasProgress = Object.values(state.chapters).some((c) => c.status !== 'not_started')
  const closeDrawer = useTutorialUi((s) => s.closeDrawer)
  const inT18 = active && step.chapter === 'T18'
  const root = useCompleteRoot()

  const guide = (
    <div className="zupa-app t0">
      {rect && spotlit && <Spotlight rect={rect} />}
      {active && step.chapter === 'T01' && placement.anchor === 'tutorial-account-status' && <AccountStatusCard user={user} />}
      {drawerOpen && (
        <ChapterDrawer
          state={state}
          hasProgress={hasProgress}
          onClose={closeDrawer}
          onRestart={(chapter) => {
            emitTutorialEvent('chapter-selected', { workspaceId: state.workspaceId })
            send({ type: 'RESTART_CHAPTER', chapter })
            if (chapter !== 'T18') closeDrawer()
          }}
          onResume={() => {
            emitTutorialEvent('tutorial-resumed', { workspaceId: state.workspaceId })
            if (['idle', 'offer', 'finished'].includes(state.mode)) dispatch({ type: 'START', at: currentSeq(), workspaceId })
            else send({ type: 'RESUME' })
            if (!inT18) closeDrawer()
          }}
          onStartOver={() => {
            send({ type: 'START_OVER' })
            closeDrawer()
          }}
        />
      )}
      {state.mode === 'offer' && (
        <WelcomeOffer
          mobile={!desktop}
          onStart={() => dispatch({ type: 'START', at: currentSeq(), workspaceId })}
          onLater={() => dispatch({ type: 'LATER' })}
        />
      )}
      {active && (
        <StepCard
          state={state}
          send={send}
          side={side}
          mobile={!desktop}
          onOpenGuide={() => useTutorialUi.getState().openDrawer()}
        />
      )}
    </div>
  )
  return root ? createPortal(guide, root) : guide
}

// The host mounts beside AppShell; prototype.css is scoped under #zupa-complete, so the guide renders inside it when it exists.
function useCompleteRoot() {
  const [root, setRoot] = useState<HTMLElement | null>(null)
  useEffect(() => {
    const find = () => setRoot(document.getElementById('zupa-complete'))
    find()
    const observer = new MutationObserver(find)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])
  return root
}
